import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { getAllBrackets } from '../bracketCache';
import { collection, query, where, getDocs, getDoc, doc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { analytics, logEvent } from '../firebase';

const C = {
  bg:'#0B1928', bgCard:'#132030',
  border:'rgba(255,255,255,0.09)', borderMid:'rgba(255,255,255,0.14)',
  orange:'#FF6B00', orangeDim:'rgba(255,107,0,0.12)', orangeBorder:'rgba(255,107,0,0.35)',
  textPrimary:'#F0F4F8', textSecondary:'#8496A8', textMuted:'#5A7A9A',
  green:'#00C851', greenDim:'rgba(0,200,81,0.10)', greenBorder:'rgba(0,200,81,0.28)',
  blue:'#3B82F6', blueDim:'rgba(59,130,246,0.12)',
};
const font = `'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif`;

const STREAK_MILESTONES = [
  { days:3,   icon:'🔥', label:'3-Day Streak'   },
  { days:7,   icon:'⭐', label:'Week Warrior'   },
  { days:14,  icon:'🎬', label:'2-Week Streak'  },
  { days:30,  icon:'📚', label:'Monthly Legend' },
  { days:50,  icon:'🎮', label:'50-Day Streak'  },
  { days:100, icon:'🏆', label:'Century Streak' },
];

const REFERRAL_MILESTONES = [
  { count:1,  icon:'🤝', label:'First Recruit'  },
  { count:5,  icon:'👥', label:'Connector'      },
  { count:10, icon:'🌟', label:'Ambassador'     },
  { count:25, icon:'🚀', label:'Recruiter'      },
];

// ── EMAIL PREFERENCES ─────────────────────────────────────────────────────────
const EMAIL_PREFS_CONFIG = [
  { key:'newBracket',    label:'New Bracket Alert',  desc:"Daily email when today's bracket goes live (7am ET)", icon:'🏆' },
  { key:'reminders',    label:'Streak Reminders',    desc:"Late reminder if you haven't played yet (11pm ET)",   icon:'⚠️' },
  { key:'streakReminder', label:'Streak Reminder',     desc:'Daily reminder if you have not played yet (11pm ET)',    ico:'⏰' },
  { key:'streakReminder', label:'Streak Reminder',     desc:'Daily reminder if you have not played yet (11pm ET)',    ico:'⏰' },
  { key:'milestones',   label:'Streak Milestones',   desc:'Email when you hit a streak milestone (3, 7, 30 days...)', icon:'🔥' },
  { key:'weeklySummary',label:'Weekly Summary',       desc:'Your weekly stats recap every Sunday morning',        icon:'📊' },
];

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{ width:'44px', height:'26px', borderRadius:'13px', cursor:'pointer', backgroundColor:value?C.orange:C.bgCard, border:`1px solid ${value?C.orange:C.border}`, position:'relative', transition:'background-color 0.2s, border-color 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:'3px', left:value?'21px':'3px', width:'18px', height:'18px', borderRadius:'50%', backgroundColor:'#FFFFFF', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }}/>
    </div>
  );
}

function EmailPreferences({ user, showToast }) {
  const [prefs,  setPrefs]  = useState({ newBracket:true, streakReminder:false, milestones:true, weeklySummary:false });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'userProfiles', user.uid));
        if (snap.exists() && snap.data().emailPrefs) setPrefs(p => ({ ...p, ...snap.data().emailPrefs }));
      } catch(e) { console.error(e); }
      setLoaded(true);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = async (key, val) => {
    const newPrefs = { ...prefs, [key]: val };
    setPrefs(newPrefs);
    setSaving(true);
    try {
      await setDoc(doc(db, 'userProfiles', user.uid), { emailPrefs: newPrefs }, { merge:true });
    } catch(e) {
      console.error(e);
      showToast('Failed to save preference. Try again.', 'error');
      setPrefs(prefs);
    }
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'16px', marginBottom:'12px' }}>
      <div style={{ fontSize: '16px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'4px' }}>Email Notifications</div>
      <div style={{ fontSize: '16px', color:C.textMuted, marginBottom:'14px' }}>Sent to {user.email}</div>
      <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
        {EMAIL_PREFS_CONFIG.map((pref, idx) => (
          <div key={pref.key} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 0', borderBottom:idx<EMAIL_PREFS_CONFIG.length-1?`1px solid ${C.border}`:'none' }}>
            <span style={{ fontSize:'18px', flexShrink:0 }}>{pref.icon}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textPrimary }}>{pref.label}</div>
              <div style={{ fontSize: '16px', color:C.textMuted, marginTop:'2px' }}>{pref.desc}</div>
            </div>
            <Toggle value={prefs[pref.key] !== false} onChange={val => handleToggle(pref.key, val)}/>
          </div>
        ))}
      </div>
      {saving && <div style={{ fontSize: '16px', color:C.textMuted, textAlign:'center', marginTop:'8px' }}>Saving...</div>}
    </div>
  );
}

// ── REFERRAL BADGES ───────────────────────────────────────────────────────────
function ReferralBadges({ invitesSent, invitesAccepted }) {
  if (invitesSent === 0 && invitesAccepted === 0) return null;
  return (
    <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'16px', marginBottom:'12px' }}>
      <div style={{ fontSize: '16px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'4px' }}>Referral Badges</div>
      <div style={{ fontSize: '16px', color:C.textMuted, marginBottom:'14px' }}>
        <span style={{ color:C.textPrimary, fontWeight:'700' }}>{invitesSent}</span> invited · <span style={{ color:C.green, fontWeight:'700' }}>{invitesAccepted}</span> joined
      </div>
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'12px' }}>
        {REFERRAL_MILESTONES.map(m => {
          const earned = invitesAccepted >= m.count;
          return (
            <div key={m.count} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', padding:'10px 12px', borderRadius:'10px', backgroundColor:earned?C.orangeDim:C.bgCard, border:`1px solid ${earned?C.orangeBorder:C.border}`, opacity:earned?1:0.35, minWidth:'52px' }}>
              <span style={{ fontSize:'20px' }}>{m.icon}</span>
              <span style={{ fontSize: '16px', fontWeight:'700', color:earned?C.orange:C.textMuted, textAlign:'center', lineHeight:'1.2' }}>{m.label}</span>
            </div>
          );
        })}
      </div>
      {/* Next milestone hint */}
      {(() => {
        const next = REFERRAL_MILESTONES.find(m => invitesAccepted < m.count);
        if (!next) return <div style={{ fontSize: '16px', color:C.green }}>🚀 All referral badges earned!</div>;
        return (
          <div style={{ fontSize: '16px', color:C.textSecondary }}>
            {next.count - invitesAccepted} more accepted invite{next.count - invitesAccepted !== 1 ? 's' : ''} to earn <span style={{ color:C.orange, fontWeight:'700' }}>{next.icon} {next.label}</span>
          </div>
        );
      })()}
    </div>
  );
}

// ── PROFILE HEADER ────────────────────────────────────────────────────────────
function ProfileHeader({ user, showToast, isPro = false }) {
  const [profile,        setProfile]        = useState({ bio:'', avatarUrl:user.photoURL||null, username:'', city:'', gender:'', ageRange:'' });
  const [editingBio,     setEditingBio]     = useState(false);
  const [bioInput,       setBioInput]       = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileInput,   setProfileInput]   = useState({});
  const [saving,         setSaving]         = useState(false);
  const [uploading,      setUploading]      = useState(false);
  const [usernameError,  setUsernameError]  = useState('');
  const fileInputRef = React.useRef(null);
  const GENDER_OPTIONS = ['Prefer not to say','Male','Female','Non-binary'];
  const AGE_OPTIONS    = ['Prefer not to say','Under 18','18-24','25-34','35-44','45-54','55+'];

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db,'userProfiles',user.uid));
        if (snap.exists()) {
          const d = snap.data();
          setProfile(prev => ({ ...prev, bio:d.bio||'', avatarUrl:d.avatarUrl||user.photoURL||null, username:d.username||'', city:d.city||'', gender:d.gender||'', ageRange:d.ageRange||'' }));
          setBioInput(d.bio||'');
        }
      } catch (err) { console.error(err); }
    };
    load();
  }, [user.uid]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5*1024*1024) { showToast('Image must be under 5MB.', 'error'); return; }
    setUploading(true);
    try {
      const storage = getStorage();
      const storageRef = ref(storage, `avatars/${user.uid}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setProfile(prev => ({ ...prev, avatarUrl:url }));
      await setDoc(doc(db,'userProfiles',user.uid), { avatarUrl:url }, { merge:true });
      showToast('Avatar updated!', 'success');
    } catch (err) { console.error(err); showToast('Upload failed. Please try again.', 'error'); }
    setUploading(false);
  };

  const handleSaveBio = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db,'userProfiles',user.uid), { bio:bioInput.trim() }, { merge:true });
      setProfile(prev => ({ ...prev, bio:bioInput.trim() }));
      setEditingBio(false);
      logEvent(analytics, 'profile_updated', { field:'bio' });
      showToast('Bio saved!', 'success');
    } catch (err) { console.error(err); showToast('Failed to save bio. Try again.', 'error'); }
    setSaving(false);
  };

  const handleSaveProfile = async () => {
    const uname = (profileInput.username||'').trim();
    if (!uname)                { setUsernameError('Username is required'); return; }
    if (!profileInput.gender)  { setUsernameError('Please select a gender'); return; }
    if (!profileInput.ageRange){ setUsernameError('Please select an age range'); return; }
    if (uname !== profile.username) {
      try {
        const q = query(collection(db,'userProfiles'), where('username','==',uname));
        const snap = await getDocs(q);
        if (snap.docs.filter(d => d.id !== user.uid).length > 0) { setUsernameError('Username already taken'); return; }
      } catch (err) { console.error(err); }
    }
    setSaving(true);
    try {
      const data = { username:uname, city:(profileInput.city||'').trim(), gender:profileInput.gender, ageRange:profileInput.ageRange };
      await setDoc(doc(db,'userProfiles',user.uid), data, { merge:true });
      setProfile(prev => ({ ...prev, ...data }));
      setEditingProfile(false);
      logEvent(analytics, 'profile_updated', { field:'profile' });
      showToast('Profile saved!', 'success');
    } catch (err) { console.error(err); showToast('Failed to save profile. Try again.', 'error'); }
    setSaving(false);
  };

  const displayName = profile.username || user.displayName || user.email?.split('@')[0] || 'Player';
  const inputStyle  = { width:'100%', boxSizing:'border-box', backgroundColor:C.bgCard, border:`1px solid ${C.borderMid}`, borderRadius:'8px', padding:'10px 12px', fontSize: '16px', color:C.textPrimary, fontFamily:font, outline:'none' };
  const selectStyle = { ...inputStyle, appearance:'none', cursor:'pointer', backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235A7A9A' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center' };

  return (
    <div style={{ backgroundColor:C.bgCard, borderRadius:'16px', border:`1px solid ${C.border}`, padding:'20px', marginBottom:'16px', textAlign:'center' }}>
      <div style={{ position:'relative', display:'inline-block', marginBottom:'12px' }}>
        <div onClick={() => fileInputRef.current?.click()} style={{ width:'80px', height:'80px', borderRadius:'50%', overflow:'hidden', border:isPro?`3px solid ${C.orange}`:`2px solid rgba(255,107,0,0.4)`, boxShadow:isPro?`0 0 0 3px rgba(255,107,0,0.15)`:'none', cursor:'pointer', margin:'0 auto', backgroundColor:C.bgCard, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {profile.avatarUrl ? <img src={profile.avatarUrl} alt="avatar" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <span style={{ fontSize:'32px' }}>👤</span>}
        </div>
        <div onClick={() => fileInputRef.current?.click()} style={{ position:'absolute', bottom:0, right:0, backgroundColor:C.orange, borderRadius:'50%', width:'24px', height:'24px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize: '16px' }}>
          {uploading ? '⏳' : '📷'}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{ display:'none' }}/>
      </div>

      <div style={{ fontSize:'17px', fontWeight:'700', color:C.textPrimary, marginBottom:'2px', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
        {displayName}
        {isPro && <span style={{ display:'inline-flex', alignItems:'center', gap:'3px', background:'rgba(255,107,0,0.12)', border:'0.5px solid rgba(255,107,0,0.3)', borderRadius:'20px', padding:'2px 8px', fontSize: '16px', fontWeight:'700', color:C.orange }}>⚡ PRO</span>}
      </div>
      

            <div onClick={() => { setProfileInput({ username:profile.username, city:profile.city, gender:profile.gender, ageRange:profile.ageRange }); setUsernameError(''); setEditingProfile(true); }} style={{ display:'inline-flex', alignItems:'center', gap:'4px', cursor:'pointer', marginBottom:'12px' }}>
        <span style={{ fontSize: '16px', color:C.orange, fontWeight:'700' }}>✏️ EDIT PROFILE</span>
      </div>

      {/* Bio hidden for privacy */}
            {editingProfile && (
        <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, backgroundColor:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ backgroundColor:C.bgCard, borderRadius:'20px 20px 0 0', padding:'24px 20px', width:'100%', maxWidth:'480px', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
              <div style={{ fontSize:'16px', fontWeight:'800', color:C.textPrimary }}>Edit Profile</div>
              <div onClick={() => setEditingProfile(false)} style={{ fontSize:'20px', color:C.textMuted, cursor:'pointer' }}>×</div>
            </div>
            <div style={{ marginBottom:'14px', textAlign:'left' }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Username</div>
              <input type="text" value={profileInput.username||''} onChange={e => { setProfileInput(p => ({ ...p, username:e.target.value })); setUsernameError(''); }} placeholder="Pick a username..." maxLength={30} style={inputStyle}/>
              {usernameError && <div style={{ fontSize: '16px', color:'#FF4444', marginTop:'4px' }}>{usernameError}</div>}
            </div>
            <div style={{ marginBottom:'14px', textAlign:'left' }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Email</div>
              <input type="text" value={'••••••••@••••••••'} readOnly style={{ ...inputStyle, opacity:0.5, cursor:'not-allowed' }}/>
            </div>
            <div style={{ marginBottom:'14px', textAlign:'left' }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>City</div>
              <input type="text" value={profileInput.city||''} onChange={e => setProfileInput(p => ({ ...p, city:e.target.value }))} placeholder="e.g. Chicago, IL" maxLength={50} style={inputStyle}/>
            </div>
            <div style={{ marginBottom:'14px', textAlign:'left' }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Gender</div>
              <select value={profileInput.gender||''} onChange={e => setProfileInput(p => ({ ...p, gender:e.target.value }))} style={selectStyle}>
                <option value="">Select...</option>
                {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:'24px', textAlign:'left' }}>
              <div style={{ fontSize: '16px', fontWeight:'700', color:C.textMuted, letterSpacing:'1px', textTransform:'uppercase', marginBottom:'6px' }}>Age Range</div>
              <select value={profileInput.ageRange||''} onChange={e => setProfileInput(p => ({ ...p, ageRange:e.target.value }))} style={selectStyle}>
                <option value="">Select...</option>
                {AGE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={handleSaveProfile} disabled={saving} style={{ flex:1, backgroundColor:saving?'rgba(255,107,0,0.5)':C.orange, color:'#fff', border:'none', borderRadius:'12px', padding:'14px', fontSize: '16px', fontWeight:'700', fontFamily:font, cursor:saving?'not-allowed':'pointer' }}>{saving?'Saving...':'Save Profile'}</button>
              <button onClick={() => setEditingProfile(false)} style={{ flex:1, backgroundColor:'transparent', color:C.textMuted, border:`1px solid rgba(255,255,255,0.09)`, borderRadius:'12px', padding:'14px', fontSize: '16px', cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackSection({ user, showToast }) {
  const [open,       setOpen]       = useState(false);
  const [type,       setType]       = useState('General');
  const [message,    setMessage]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [focused,    setFocused]    = useState(false);
  const TYPES = ['Bug Report','Feature Request','General'];

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db,'feedback'), { userId:user.uid, email:user.email, type, message:message.trim(), createdAt:serverTimestamp(), read:false });
      setSubmitted(true);
      setMessage('');
      showToast('Feedback sent! Thanks.', 'success');
      setTimeout(() => { setSubmitted(false); setOpen(false); }, 2500);
    } catch (err) { console.error(err); showToast('Failed to send feedback. Try again.', 'error'); }
    setSubmitting(false);
  };

  return (
    <div style={{ marginBottom:'12px' }}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={{ width:'100%', backgroundColor:'transparent', border:`1px solid rgba(255,255,255,0.09)`, borderRadius:'12px', padding:'14px', fontSize: '16px', fontWeight:'700', fontFamily:font, letterSpacing:'1px', color:C.textSecondary, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
          <span style={{ fontSize:'16px' }}>💬</span> Send Feedback
        </button>
      ) : (
        <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'16px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' }}>
            <div style={{ fontSize: '16px', fontWeight:'700', color:C.textPrimary }}>Send Feedback</div>
            <div onClick={() => setOpen(false)} style={{ fontSize:'18px', color:C.textMuted, cursor:'pointer' }}>×</div>
          </div>
          <div style={{ display:'flex', gap:'6px', marginBottom:'12px' }}>
            {TYPES.map(t => <div key={t} onClick={() => setType(t)} style={{ flex:1, textAlign:'center', padding:'6px 4px', borderRadius:'8px', fontSize: '16px', fontWeight:'700', cursor:'pointer', backgroundColor:type===t?C.orangeDim:C.bgCard, border:`1px solid ${type===t?C.orangeBorder:C.border}`, color:type===t?C.orange:C.textMuted }}>{t}</div>)}
          </div>
          <textarea value={message} onChange={e => setMessage(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder="Tell us what's on your mind..." rows={4} maxLength={500} style={{ width:'100%', backgroundColor:C.bgCard, border:`1px solid ${focused?C.orangeBorder:C.border}`, borderRadius:'8px', padding:'10px 12px', fontSize: '16px', color:C.textPrimary, fontFamily:font, outline:'none', resize:'none', lineHeight:'1.5', boxSizing:'border-box' }}/>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:'4px', marginBottom:'12px' }}>
            <span style={{ fontSize: '16px', color:C.textMuted }}>{message.length}/500</span>
          </div>
          {submitted
            ? <div style={{ backgroundColor:C.greenDim, border:`1px solid rgba(0,200,81,0.3)`, borderRadius:'10px', padding:'12px', textAlign:'center', fontSize: '16px', color:C.green, fontWeight:'700' }}>✔ Thanks for your feedback!</div>
            : <button onClick={handleSubmit} disabled={submitting||!message.trim()} style={{ width:'100%', backgroundColor:submitting||!message.trim()?'rgba(255,107,0,0.3)':C.orange, color:'#FFF', border:'none', borderRadius:'10px', padding:'13px', fontSize: '16px', fontWeight:'700', fontFamily:font, cursor:submitting||!message.trim()?'not-allowed':'pointer' }}>{submitting?'Sending...':'Submit'}</button>
          }
        </div>
      )}
    </div>
  );
}

const ADMIN_EMAIL = 'jelst';


// ── PRO BADGE HELPER (use this wherever username is rendered) ─────────────────
export function ProBadge() {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'2px', backgroundColor:'rgba(255,107,0,0.18)', border:'1px solid rgba(255,107,0,0.45)', borderRadius:'10px', padding:'2px 7px', fontSize: '16px', fontWeight:'800', color:'#FF6B00', letterSpacing:'0.5px', verticalAlign:'middle', marginLeft:'5px' }}>
      ⚡ PRO
    </span>
  );
}

// ── PRO UPGRADE SECTION ───────────────────────────────────────────────────────
const FUNCTIONS_BASE = 'https://us-central1-bracketz-d95e2.cloudfunctions.net';

const PRO_PERKS = [
  { icon:'⚡', label:'Pro Badge',          desc:'Displayed next to your username everywhere'      },
  { icon:'🔒', label:'Streak Freeze',      desc:'One free pass per month — protect your streak'  },
  { icon:'📅', label:'Early Access',       desc:"See tomorrow's bracket category at 9pm tonight"  },
  { icon:'📊', label:'Full Crowd Analytics', desc:'Per-matchup breakdown for every round'        },
  { icon:'👥', label:'Private Groups',     desc:'Create exclusive groups for your crew'           },
  { icon:'🎯', label:'Exclusive Brackets', desc:'Pro-only weekly brackets dropping soon'          },
];

function ProUpgradeSection({ user, isPro, showToast }) {
  const [loading,        setLoading]        = useState(false);
  const [portalLoading,  setPortalLoading]  = useState(false);
  const [streakFreezes,  setStreakFreezes]  = useState(0);
  const [planType,       setPlanType]       = useState('monthly'); // 'monthly' | 'annual'
  const [giftEmail,      setGiftEmail]      = useState('');
  const [giftLoading,    setGiftLoading]    = useState(false);
  const [giftSent,       setGiftSent]       = useState(false);
  const [showGift,       setShowGift]       = useState(false);
  const [freezeUsed,     setFreezeUsed]     = useState(false);
  const [usingFreeze,    setUsingFreeze]    = useState(false);

  useEffect(() => {
    if (!isPro) return;
    const loadFreezeData = async () => {
      try {
        const snap = await getDoc(doc(db, 'userProfiles', user.uid));
        if (snap.exists()) {
          const d = snap.data();
          const month = new Date().toISOString().slice(0, 7); // "2026-04"
          setStreakFreezes(d.streakFreezesRemaining ?? 1);
          setFreezeUsed(d.streakFreezeUsedMonth === month);
        }
      } catch(e) { console.error(e); }
    };
    loadFreezeData();
  }, [isPro, user.uid]);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, email: user.email }),
      });
      const data = await res.json();
      if (data.url) {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.url });
        } else {
          window.location.href = data.url;
        }
      } else {
        showToast('Could not start checkout. Try again.', 'error');
      }
    } catch(e) {
      console.error(e);
      showToast('Could not reach checkout. Check your connection.', 'error');
    }
    setLoading(false);
  };


  const handleUpgradeAnnual = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, email: user.email, plan: 'annual' }),
      });
      const data = await res.json();
      if (data.url) {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.url });
        } else {
          window.location.href = data.url;
        }
      } else { showToast('Could not start checkout. Try again.', 'error'); }
    } catch(e) { console.error(e); showToast('Could not reach checkout.', 'error'); }
    setLoading(false);
  };

  const handleGiftPro = async () => {
    if (!giftEmail.trim() || !giftEmail.includes('@')) {
      showToast('Enter a valid email address.', 'error'); return;
    }
    setGiftLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, email: user.email, plan: 'gift', giftEmail: giftEmail.trim() }),
      });
      const data = await res.json();
      if (data.url) {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          const { Browser } = await import('@capacitor/browser');
          await Browser.open({ url: data.url });
        } else {
          window.location.href = data.url;
        }
      }
      else { showToast('Could not start gift checkout. Try again.', 'error'); }
    } catch(e) { console.error(e); showToast('Could not reach checkout.', 'error'); }
    setGiftLoading(false);
  };

  const handleManage = async () => {
    setPortalLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createPortalSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast('Could not open billing portal. Try again.', 'error');
      }
    } catch(e) {
      console.error(e);
      showToast('Could not reach billing portal.', 'error');
    }
    setPortalLoading(false);
  };

  const handleUseFreeze = async () => {
    if (freezeUsed || streakFreezes < 1) return;
    setUsingFreeze(true);
    try {
      const month = new Date().toISOString().slice(0, 7);
      await setDoc(doc(db, 'userProfiles', user.uid), {
        streakFreezeUsedMonth: month,
        streakFreezeActive:    true,
        streakFreezesRemaining: 0,
      }, { merge: true });
      setFreezeUsed(true);
      setStreakFreezes(0);
      showToast('❄️ Streak Freeze activated! You\'re covered for today.', 'success');
    } catch(e) {
      console.error(e);
      showToast('Failed to activate freeze. Try again.', 'error');
    }
    setUsingFreeze(false);
  };

  // ── PRO ACTIVE STATE ──────────────────────────────────────────────────────
  if (isPro) {
    return (
      <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:'1px solid rgba(255,107,0,0.35)', padding:'16px', marginBottom:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight:'700', color:'#FF6B00', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'3px' }}>Bracketz Pro</div>
            <div style={{ fontSize: '16px', color:'#8FA3C0' }}>Your membership is active ✓</div>
          </div>
          <div style={{ backgroundColor:'rgba(255,107,0,0.18)', border:'1px solid rgba(255,107,0,0.45)', borderRadius:'10px', padding:'5px 10px', fontSize: '16px', fontWeight:'800', color:'#FF6B00' }}>⚡ PRO</div>
        </div>

        {/* Streak Freeze */}
        <div style={{ backgroundColor:C.bgCard, borderRadius:'10px', padding:'12px', marginBottom:'10px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px' }}>
            <div style={{ fontSize: '16px', fontWeight:'700', color:'#F0F4F8' }}>🔒 Streak Freeze</div>
            <div style={{ fontSize: '16px', color: freezeUsed ? '#5A7A9A' : '#00C851', fontWeight:'700' }}>
              {freezeUsed ? 'Used this month' : `${streakFreezes} available`}
            </div>
          </div>
          <div style={{ fontSize: '16px', color:'#8FA3C0', marginBottom:'10px', lineHeight:'1.5' }}>
            Miss a day without breaking your streak. Resets every month.
          </div>
          <button
            onClick={handleUseFreeze}
            disabled={freezeUsed || streakFreezes < 1 || usingFreeze}
            style={{
              width:'100%', border:'none', borderRadius:'8px', padding:'10px',
              fontSize: '16px', fontWeight:'700', fontFamily:font, cursor: freezeUsed||streakFreezes<1 ? 'not-allowed' : 'pointer',
              backgroundColor: freezeUsed||streakFreezes<1 ? 'rgba(255,255,255,0.09)' : 'rgba(0,200,81,0.15)',
              color: freezeUsed||streakFreezes<1 ? '#5A7A9A' : '#00C851',
              border: `1px solid ${freezeUsed||streakFreezes<1 ? 'rgba(255,255,255,0.09)' : 'rgba(0,200,81,0.3)'}`,
            }}
          >
            {usingFreeze ? 'Activating...' : freezeUsed ? '❄️ Freeze already used this month' : '❄️ Activate Freeze for Today'}
          </button>
        </div>

        <button
          onClick={handleManage}
          disabled={portalLoading}
          style={{ width:'100%', backgroundColor:'transparent', border:'1px solid rgba(255,255,255,0.09)', borderRadius:'10px', padding:'11px', fontSize: '16px', fontWeight:'700', fontFamily:font, color:'#8FA3C0', cursor:'pointer' }}
        >
          {portalLoading ? 'Opening...' : '⚙️ Manage Subscription'}
        </button>
      </div>
    );
  }

  // ── UPGRADE UPSELL STATE ──────────────────────────────────────────────────
  return (
    <div style={{ marginBottom:'12px', borderRadius:'16px', overflow:'hidden', border:'1px solid rgba(255,107,0,0.35)', position:'relative' }}>
      {/* Gold header bar */}
      <div style={{ background:'linear-gradient(135deg, #FF6B00 0%, #FF9340 100%)', padding:'16px 18px' }}>
        <div style={{ fontSize: '16px', fontWeight:'800', color:'rgba(255,255,255,0.7)', letterSpacing:'2px', textTransform:'uppercase', marginBottom:'4px' }}>Bracketz Pro</div>
        <div style={{ fontSize:'18px', fontWeight:'900', color:'#F0F4F8', marginBottom:'2px' }}>Upgrade your game</div>
        <div style={{ fontSize: '16px', color:'rgba(255,255,255,0.8)' }}>{planType === 'annual' ? '$24.99 / year — save 30%' : '$2.99 / month — cancel anytime'}</div>
      </div>

      <div style={{ backgroundColor:C.bgCard, padding:'16px' }}>
        {/* Perk list */}
        <div style={{ display:'flex', flexDirection:'column', gap:'0', marginBottom:'16px' }}>
          {PRO_PERKS.map((perk, idx) => (
            <div key={perk.label} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 0', borderBottom: idx < PRO_PERKS.length-1 ? `1px solid ${C.border}` : 'none' }}>
              <span style={{ fontSize:'18px', flexShrink:0, width:'24px', textAlign:'center' }}>{perk.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize: '16px', fontWeight:'700', color:'#F0F4F8' }}>{perk.label}</div>
                <div style={{ fontSize: '16px', color:'#5A7A9A', marginTop:'1px' }}>{perk.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Plan toggle */}
        <div style={{ display:'flex', backgroundColor:'rgba(0,0,0,0.2)', borderRadius:'10px', padding:'3px', marginBottom:'14px', border:`0.5px solid ${C.border}` }}>
          {[
            { key:'monthly', label:'Monthly', price:'$2.99/mo' },
            { key:'annual',  label:'Annual',  price:'$24.99/yr', badge:'Save 30%' },
          ].map(p => (
            <div key={p.key} onClick={() => setPlanType(p.key)} style={{ flex:1, padding:'8px', textAlign:'center', borderRadius:'8px', cursor:'pointer', backgroundColor:planType===p.key?C.orange:'transparent', transition:'all 0.15s', position:'relative' }}>
              {p.badge && planType !== p.key && <div style={{ position:'absolute', top:'-8px', right:'4px', fontSize: '16px', fontWeight:'800', backgroundColor:'#00C851', color:'#fff', padding:'1px 5px', borderRadius:'6px' }}>{p.badge}</div>}
              <div style={{ fontSize: '16px', fontWeight:'700', color:planType===p.key?'#fff':C.textMuted }}>{p.label}</div>
              <div style={{ fontSize: '16px', color:planType===p.key?'rgba(255,255,255,0.8)':C.textMuted }}>{p.price}</div>
            </div>
          ))}
        </div>

        {planType === 'annual' && (
          <div style={{ backgroundColor:'rgba(0,200,81,0.08)', border:'1px solid rgba(0,200,81,0.25)', borderRadius:'10px', padding:'10px 14px', marginBottom:'14px', fontSize: '16px', color:'#00C851', textAlign:'center' }}>
            💰 You save $10.89 per year vs monthly
          </div>
        )}

        <button
          onClick={planType === 'annual' ? handleUpgradeAnnual : handleUpgrade}
          disabled={loading}
          style={{ width:'100%', background: loading ? 'rgba(255,107,0,0.5)' : 'linear-gradient(135deg, #FF6B00, #FF9340)', color:'#fff', border:'none', borderRadius:'12px', padding:'16px', fontSize: '16px', fontWeight:'800', cursor: loading ? 'not-allowed' : 'pointer', fontFamily:font, letterSpacing:'0.5px', marginBottom:'8px' }}
        >
          {loading ? 'Starting checkout...' : planType === 'annual' ? '⚡ Get Pro Annual — $24.99/yr' : '⚡ Upgrade to Pro — $2.99/mo'}
        </button>
        <div style={{ textAlign:'center', fontSize: '16px', color:'#5A7A9A', marginTop:'4px', marginBottom:'16px' }}>Secure payment via Stripe · Cancel anytime</div>

        {/* Gift Pro */}
        <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:'14px' }}>
          <div onClick={() => setShowGift(g => !g)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', marginBottom: showGift ? '12px' : '0' }}>
            <div style={{ fontSize: '16px', fontWeight:'700', color:C.textPrimary }}>🎁 Gift Pro to a friend</div>
            <span style={{ fontSize: '16px', color:C.textMuted, transform:showGift?'rotate(180deg)':'rotate(0deg)', transition:'transform 0.2s' }}>▾</span>
          </div>
          {showGift && (
            <div>
              <div style={{ fontSize: '16px', color:C.textMuted, marginBottom:'10px', lineHeight:'1.5' }}>
                Give a friend 1 month of Pro ($2.99). You'll get a <strong style={{ color:C.orange }}>10% discount</strong> on your next month and unlock the <strong style={{ color:'#FFD700' }}>🎁 Generous badge</strong>.
              </div>
              <input
                type="email"
                value={giftEmail}
                onChange={e => setGiftEmail(e.target.value)}
                placeholder="Friend's email address"
                style={{ width:'100%', backgroundColor:C.bgCard, border:`0.5px solid ${C.border}`, borderRadius:'8px', padding:'10px 12px', fontSize: '16px', color:C.textPrimary, fontFamily:font, outline:'none', boxSizing:'border-box', marginBottom:'10px' }}
              />
              {giftSent ? (
                <div style={{ textAlign:'center', padding:'12px', backgroundColor:'rgba(0,200,81,0.1)', borderRadius:'10px', fontSize: '16px', color:'#00C851', fontWeight:'700' }}>
                  ✅ Gift sent! Badge unlocked.
                </div>
              ) : (
                <button
                  onClick={handleGiftPro}
                  disabled={giftLoading || !giftEmail.includes('@')}
                  style={{ width:'100%', backgroundColor:giftLoading||!giftEmail.includes('@')?'rgba(255,215,0,0.3)':'rgba(255,215,0,0.15)', color:'#FFD700', border:'1px solid rgba(255,215,0,0.4)', borderRadius:'10px', padding:'12px', fontSize: '16px', fontWeight:'700', fontFamily:font, cursor:giftLoading||!giftEmail.includes('@')?'not-allowed':'pointer' }}
                >
                  {giftLoading ? 'Processing...' : '🎁 Send Gift — $2.99'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Profile({ user, streakCount: streakCountProp, onSignOut, onNavigateAdmin, onNavigateHistory, showToast }) {
  const [stats,   setStats]   = useState({ submitted:0, streakCount:0, longestStreak:0, invitesSent:0, invitesAccepted:0 });
  const [recent,  setRecent]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPro,   setIsPro]   = useState(false);
  const username = user.email.split('@')[0];
  const isAdmin  = ['jelston314@gmail.com', 'danlandia200@gmail.com'].includes(user.email.toLowerCase());

  // Check for successful Pro upgrade redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pro_success') === '1') {
      showToast('⚡ Welcome to Bracketz Pro! Your perks are now active.', 'success');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const q    = query(collection(db,'userBrackets'), where('userId','==',user.uid));
      const snap = await getDocs(q);
      const all  = snap.docs.map(d => ({ id:d.id,...d.data() }));
      const done = all.filter(b => b.isSubmitted);

      const profileSnap   = await getDoc(doc(db,'userProfiles',user.uid));
      const profileData   = profileSnap.exists() ? profileSnap.data() : {};
      const streakCount   = streakCountProp !== undefined ? streakCountProp : (profileData.streakCount || 0);
      const longestStreak = profileData.longestStreak || 0;
      const invitesSent   = profileData.invitesSent || 0;
      const invitesAccepted = profileData.invitesAccepted || 0;

      setIsPro(profileData.isPro === true);
      setStats({ submitted:done.length, streakCount, longestStreak, invitesSent, invitesAccepted });

      const sorted = done.sort((a,b) => (b.submittedAt?.seconds||0) - (a.submittedAt?.seconds||0)).slice(0,5);
      const bSnap  = await getAllBrackets();
      const bMap   = {};
      bSnap.forEach(b => { bMap[b.id] = b; });
      setRecent(sorted.map(ub => ({ ...ub, category:bMap[ub.bracketId]?.category||'Unknown Bracket', date:bMap[ub.bracketId]?.date||'' })));
      setLoading(false);
    } catch (err) { console.error(err); setLoading(false); }
  };

  const earnedMilestones = STREAK_MILESTONES.filter(m => stats.longestStreak >= m.days);

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'300px' }}><div style={{ color:C.textMuted, fontSize: '16px', fontFamily:font }}>Loading profile...</div></div>;

  return (
    <div style={{ padding:'16px', paddingBottom:'90px', fontFamily:font }}>
      <ProfileHeader user={user} showToast={showToast} isPro={isPro}/>

      {/* Stats row — NYT style */}
      <div className='bracketz-fade-up' style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', borderTop:`0.5px solid ${C.border}`, marginBottom:'0' }}>
        {[
          { value:stats.submitted,          label:'Brackets',   click:onNavigateHistory },
          { value:stats.streakCount,         label:'Day Streak', click:null },
          { value:stats.longestStreak,       label:'Best Streak',click:null },
        ].map((s, i) => (
          <div key={i} onClick={s.click||undefined} style={{ padding:'16px 8px', textAlign:'center', borderRight:i<2?`0.5px solid ${C.border}`:'none', cursor:s.click?'pointer':'default' }}>
            <div style={{ fontSize:'24px', fontWeight:'700', color:C.orange, lineHeight:1 }}>
              {i===1 ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'4px'}}>{s.value}<svg width="14" height="16" viewBox="0 0 16 18" fill="none"><path d="M8 1C8 1 12 5 12 9C12 10.5 11.3 11.8 10.2 12.7C10.6 11.8 10.5 10.7 9.8 9.9C9.1 9.1 7.9 8.8 7 9.2C6.1 9.6 5.5 10.5 5.5 11.5C5.5 12.1 5.7 12.7 6.1 13.1C4.8 12.2 4 10.7 4 9C4 5.5 8 1 8 1Z" fill="#FF6B00"/><path d="M8 17C5.2 17 3 14.8 3 12C3 10.3 3.9 8.8 5.2 8C5.1 8.3 5 8.7 5 9C5 10.1 5.7 11.1 6.7 11.5C6.4 10.9 6.5 10.1 7 9.7C7.5 9.3 8.2 9.3 8.7 9.7C9.2 10.1 9.4 10.8 9.1 11.4C10.3 10.6 11 9.4 11 8C11 7.3 10.8 6.6 10.5 6C11.8 7 13 8.8 13 11C13 14.3 10.8 17 8 17Z" fill="#FF6B00"/></svg></span> : s.value}
            </div>
            <div style={{ fontSize: '16px', color:C.textMuted, marginTop:'5px', letterSpacing:'0.5px', textTransform:'uppercase', fontWeight:'600' }}>{s.label}</div>
            {i===0 && <div style={{ fontSize: '16px', color:C.orange, marginTop:'3px', fontWeight:'700' }}>VIEW ALL →</div>}
          </div>
        ))}
      </div>

      {/* Referral stat row */}
      {stats.invitesSent > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
          <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${C.border}`, padding:'14px', textAlign:'center' }}>
            <div style={{ fontSize:'22px', fontWeight:'800', color:C.orange, lineHeight:1 }}>{stats.invitesSent}</div>
            <div style={{ fontSize: '16px', color:C.textMuted, marginTop:'5px' }}>Friends Invited</div>
          </div>
          <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:`1px solid ${stats.invitesAccepted>0?C.greenBorder:C.border}`, padding:'14px', textAlign:'center' }}>
            <div style={{ fontSize:'22px', fontWeight:'800', color:stats.invitesAccepted>0?C.green:C.orange, lineHeight:1 }}>{stats.invitesAccepted}</div>
            <div style={{ fontSize: '16px', color:C.textMuted, marginTop:'5px' }}>Joined Bracketz</div>
          </div>
        </div>
      )}

      {/* Pro upgrade / Pro status — hidden on iOS (use IAP) */}
      {!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios') && (
        <ProUpgradeSection user={user} isPro={isPro} showToast={showToast}/>
      )}

{/* Streak badges */}
      <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'16px', marginBottom:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div style={{ fontSize: '16px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase' }}>Badge Showcase</div>
          <div style={{ fontSize: '16px', color:C.textMuted }}>{STREAK_MILESTONES.filter(m => stats.longestStreak >= m.days).length}/{STREAK_MILESTONES.length} earned</div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
          {STREAK_MILESTONES.map(m => {
            const earned = stats.longestStreak >= m.days;
            const isNext = !earned && STREAK_MILESTONES.find(x => x.days > stats.longestStreak)?.days === m.days;
            const progress = isNext ? Math.round((stats.longestStreak / m.days) * 100) : 0;
            return (
              <div key={m.days} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px', padding:'14px 8px 12px', borderRadius:'14px', backgroundColor:earned?'rgba(255,107,0,0.08)':isNext?'rgba(255,255,255,0.03)':'rgba(255,255,255,0.02)', border:`1px solid ${earned?C.orangeBorder:isNext?'rgba(255,255,255,0.12)':C.border}`, position:'relative', overflow:'hidden', transition:'all 0.2s' }}>
                {earned && <div style={{ position:'absolute', top:0, left:0, right:0, height:'2px', background:'linear-gradient(90deg, transparent, #FF6B00, transparent)' }}/>}
                <div style={{ fontSize:'32px', filter:earned?'none':'grayscale(1)', opacity:earned?1:isNext?0.5:0.25, transition:'all 0.2s' }}>{m.icon}</div>
                <div style={{ fontSize: '16px', fontWeight:'800', color:earned?C.orange:isNext?C.textSecondary:C.textMuted, textAlign:'center', lineHeight:'1.3' }}>{m.label}</div>
                <div style={{ fontSize: '16px', color:earned?'rgba(255,107,0,0.6)':C.textMuted, textAlign:'center' }}>{m.days} day streak</div>
                {earned && <div style={{ backgroundColor:'rgba(255,107,0,0.15)', border:'1px solid rgba(255,107,0,0.3)', borderRadius:'20px', padding:'2px 8px', fontSize: '16px', fontWeight:'800', color:C.orange }}>EARNED</div>}
                {isNext && (
                  <div style={{ width:'100%', marginTop:'4px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                      <span style={{ fontSize: '16px', color:C.textMuted }}>Progress</span>
                      <span style={{ fontSize: '16px', color:C.orange, fontWeight:'700' }}>{progress}%</span>
                    </div>
                    <div style={{ backgroundColor:'rgba(255,255,255,0.06)', borderRadius:'4px', height:'4px', overflow:'hidden' }}>
                      <div style={{ width:`${progress}%`, height:'100%', borderRadius:'4px', backgroundColor:C.orange, transition:'width 0.5s ease' }}/>
                    </div>
                    <div style={{ fontSize: '16px', color:C.textMuted, textAlign:'center', marginTop:'4px' }}>{m.days - stats.longestStreak} days to go</div>
                  </div>
                )}
                {!earned && !isNext && <div style={{ fontSize: '16px', color:'rgba(255,255,255,0.15)', textAlign:'center' }}>🔒 Locked</div>}
              </div>
            );
          })}
        </div>
        {stats.streakCount > 0 && (
          <div style={{ marginTop:'14px', padding:'10px 14px', backgroundColor:'rgba(255,107,0,0.06)', borderRadius:'10px', border:'1px solid rgba(255,107,0,0.2)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontSize: '16px', color:C.textSecondary }}>Current streak</span>
            <span style={{ fontSize: '16px', fontWeight:'800', color:C.orange }}>{stats.streakCount} days 🔥</span>
          </div>
        )}
      </div>

      {/* Referral badges */}
      <ReferralBadges invitesSent={stats.invitesSent} invitesAccepted={stats.invitesAccepted}/>

      {/* Recent brackets */}
      <div style={{ backgroundColor:C.bgCard, borderRadius:'14px', border:`1px solid ${C.border}`, padding:'16px', marginBottom:'12px' }}>
        <div style={{ fontSize: '16px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px' }}>Recent Brackets</div>
        {recent.length === 0
          ? <div style={{ fontSize: '16px', color:C.textMuted, textAlign:'center', padding:'16px' }}>No brackets submitted yet</div>
          : recent.map(b => (
            <div key={b.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
              <div style={{ flex:1, marginRight:'12px' }}>
                <div style={{ fontSize: '16px', color:C.textSecondary, fontWeight:'600' }}>{b.category}</div>
                {b.date && <div style={{ fontSize: '16px', color:C.textMuted, marginTop:'2px' }}>{(() => { const [y,m,d]=b.date.split('-'); return new Date(y,m-1,d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}); })()}</div>}
              </div>
              <div style={{ fontSize: '16px', color:C.green, fontWeight:'600', whiteSpace:'nowrap' }}>✔ Done</div>
            </div>
          ))
        }
      </div>

      <EmailPreferences user={user} showToast={showToast}/>
      <FeedbackSection user={user} showToast={showToast}/>

      <button
        onClick={() => {
          if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
            const { deleteUser } = require('firebase/auth');
            const { doc, deleteDoc } = require('firebase/firestore');
            const { db, auth } = require('../firebase');
            deleteDoc(doc(db, 'userProfiles', user.uid))
              .then(() => deleteUser(auth.currentUser))
              .then(() => window.location.reload())
              .catch(err => {
                if (err.code === 'auth/requires-recent-login') {
                  alert('For security, please sign out and sign back in before deleting your account.');
                } else {
                  alert('Error deleting account. Please try again or contact support at privacy@playbracketz.com');
                }
              });
          }
        }}
        style={{ width:'100%', backgroundColor:'transparent', border:`1px solid rgba(255,68,68,0.2)`, borderRadius:'12px', padding:'13px', fontSize:'14px', fontWeight:'700', fontFamily:font, letterSpacing:'1.5px', textTransform:'uppercase', color:'rgba(255,80,80,0.5)', cursor:'pointer', marginBottom:'10px' }}
      >
        Delete Account
      </button>

      <button onClick={onSignOut} style={{ width:'100%', backgroundColor:'transparent', border:`1px solid rgba(255,68,68,0.3)`, borderRadius:'12px', padding:'15px', fontSize: '16px', fontWeight:'700', fontFamily:font, letterSpacing:'1.5px', textTransform:'uppercase', color:'rgba(255,100,100,0.8)', cursor:'pointer', marginBottom:isAdmin?'10px':'0' }}>
        Sign Out
      </button>

      {isAdmin && (
        <button onClick={onNavigateAdmin} style={{ width:'100%', backgroundColor:'transparent', border:`1px solid rgba(255,255,255,0.09)`, borderRadius:'12px', padding:'10px', fontSize: '16px', fontWeight:'700', fontFamily:font, letterSpacing:'2px', textTransform:'uppercase', color:C.textMuted, cursor:'pointer', opacity:0.4 }}>
          ⚙ Admin
        </button>
      )}
    </div>
  );
}

export default Profile;
